import { isDev } from "../../../base/index.js";
import { getByPath } from "../../../dot-notation/index.js";
function createBaseFieldApi(fieldName, formApi) {
  return {
    getField: () => {
      const field = formApi.getField(fieldName);
      if (!field) {
        throw new Error(`[createBaseFieldApi] Field "${fieldName}" not found`);
      }
      return field;
    },
    change: (value) => {
      formApi.change(fieldName, value);
    },
    validate: async (force) => {
      const targetField = formApi.getField(fieldName);
      if (force || targetField?.status === "indeterminate") {
        await formApi.validate(fieldName);
        return;
      }
      return await formApi.waitForValidation();
    },
    setDisabled: (value) => {
      formApi.setFieldDisabledState(fieldName, value);
    },
    setVisible: (value) => {
      formApi.setFieldVisibleState(fieldName, value);
    },
    setRequired: (value) => {
      formApi.setFieldRequiredState(fieldName, value);
    },
    clearValidation: () => {
      formApi.clearValidation(fieldName);
    },
    addValidation: (id, validator) => {
      formApi.addValidation(id, validator, fieldName);
    },
    removeValidation: (id) => {
      formApi.removeValidation(id);
    },
    addMutation: (mutation) => {
      formApi.addFieldMutation(fieldName, mutation);
    },
    removeMutation: () => {
      return formApi.removeFieldMutation(fieldName);
    },
    addTransform: (transform) => {
      formApi.addFieldTransform(fieldName, transform);
    },
    removeTransform: () => {
      return formApi.removeFieldTransform(fieldName);
    }
  };
}
function createArrayFieldApi(fieldName, formApi) {
  return {
    ...createBaseFieldApi(fieldName, formApi),
    changeArrayElement: (index, value) => {
      formApi.changeArrayElement(fieldName, index, value);
    },
    removeArrayElements: (indexes) => {
      formApi.removeArrayElements(fieldName, indexes);
    },
    addArrayElements: (index, element, ...rest) => {
      formApi.addArrayElements(fieldName, index, element, ...rest);
    },
    swapArrayElements: (indexA, indexB) => {
      formApi.swapArrayElements(fieldName, indexA, indexB);
    },
    clearArrayElements: () => {
      formApi.clearArrayElements(fieldName);
    }
  };
}
function createFieldApi(formApi, fieldName, initialFieldConfig) {
  if (!fieldName) {
    throw new Error('[createFieldApi] expects a valid "field name"');
  }
  let field = formApi.getField(fieldName);
  if (!field) {
    const fieldValue = getByPath(formApi.getValues(), fieldName);
    const fieldInitialValue = getByPath(formApi.getInitialValues(), fieldName) ?? initialFieldConfig?.initialValue;
    if (isDev()) {
      if (fieldValue === void 0 && fieldInitialValue === void 0) {
        console.warn(
          `[createFieldApi] Initializing the field "${fieldName}" without any values in the form state. The field type will default to "field" because there's no way to infer if it's an array.`
        );
      }
      if (fieldValue !== void 0 || fieldInitialValue !== void 0) {
        if (!Array.isArray(fieldValue ?? fieldInitialValue) && initialFieldConfig?.type === "arrayField") {
          console.warn(
            `[createFieldApi] Initializing the field "${fieldName}" as "arrayField" but the data in the form state is not an array instance`
          );
        }
      }
    }
    formApi.registerField(fieldName, {
      // @ts-expect-error // FIXME: improve the type definitions of "fieldValue"
      value: fieldValue ?? fieldInitialValue,
      // @ts-expect-error // FIXME: improve the type definitions of "fieldInitialValue"
      resetValue: fieldInitialValue,
      visible: true,
      disabled: false,
      required: false,
      ...initialFieldConfig,
      status: initialFieldConfig?.status ? initialFieldConfig.status : "pristine",
      type: initialFieldConfig?.type ? initialFieldConfig.type : Array.isArray(fieldValue ?? fieldInitialValue) ? "arrayField" : "field"
    });
  }
  field = formApi.getField(fieldName);
  if (!field) {
    throw new Error(`[createFieldApi] Field "${fieldName}" not found`);
  }
  if (field.type === "arrayField") {
    const fieldArrayName = fieldName;
    return createArrayFieldApi(
      fieldArrayName,
      formApi
    );
  }
  return createBaseFieldApi(fieldName, formApi);
}
export {
  createFieldApi
};
//# sourceMappingURL=createFieldApi.js.map
